/**
 * UK Statutory Sick Pay Constants
 * Updated for 2025/26 tax year and Employment Rights Act 2024 changes
 */

// ===== 2025/26 TAX YEAR (Current Rules) =====
export const SSP_WEEKLY_RATE_2025 = 118.75;
export const LOWER_EARNINGS_LIMIT_2025 = 125; // £/week minimum to qualify

// ===== 2026/27 TAX YEAR (April 2026 Onwards) =====
export const SSP_WEEKLY_RATE_2026 = 123.25;
export const LOWER_EARNINGS_LIMIT_2026 = null; // Abolished

// ===== Backward Compatibility (Default to current year) =====
export const SSP_WEEKLY_RATE = SSP_WEEKLY_RATE_2025;
export const LOWER_EARNINGS_LIMIT = LOWER_EARNINGS_LIMIT_2025;

// ===== Daily Rates =====
export const SSP_DAILY_RATE_7_DAYS = SSP_WEEKLY_RATE / 7; // £16.96
export const SSP_DAILY_RATE_7_DAYS_2026 = SSP_WEEKLY_RATE_2026 / 7; // £17.61

// ===== 2026 Thresholds =====
export const NEW_EARNINGS_CAP_PERCENT = 0.80; // 80% of earnings for those below threshold
// When does 80% × AWE = £123.25? When AWE = £154.06
export const EIGHTY_PERCENT_THRESHOLD_2026 = SSP_WEEKLY_RATE_2026 / NEW_EARNINGS_CAP_PERCENT; // £154.0625
export const WAITING_DAYS_CURRENT = 3; // SSP starts from day 4
export const WAITING_DAYS_2026 = 0; // SSP from day 1

// Maximum SSP duration
export const MAX_SSP_WEEKS = 28;

// Default sickness absence patterns (UK hospitality averages)
export const DEFAULT_SICKNESS_PATTERN = {
  avgEpisodesPerEmployee: 2.5, // Average episodes per year
  distribution: {
    oneDay: 35,      // % of absences that are 1 day
    twoDay: 25,      // % of absences that are 2 days
    threeDay: 15,    // % of absences that are 3 days
    fourToSeven: 20, // % of absences that are 4-7 days
    eightPlus: 5,    // % of absences that are 8+ days
  },
  avgLengths: {
    fourToSeven: 5,  // Average days for 4-7 day absences
    eightPlus: 12,   // Average days for 8+ day absences
  }
};

// Default employee cohorts for hospitality
export const DEFAULT_COHORTS = [
  {
    id: 'fulltime',
    name: 'Full-time',
    description: 'Standard full-time staff on 35-40 hours',
    count: 30,
    contractedHours: 40,
    avgPickupHours: 0,
    hourlyRate: 12.50,
    qualifyingDays: 5,
    typicalShiftLength: 8,
    rotaProbability: 0.71, // 5/7 days
  },
  {
    id: 'parttime',
    name: 'Part-time (20hrs)',
    description: 'Regular part-time staff',
    count: 40,
    contractedHours: 20,
    avgPickupHours: 4,
    hourlyRate: 11.50,
    qualifyingDays: 3,
    typicalShiftLength: 6,
    rotaProbability: 0.43, // 3/7 days
  },
  {
    id: 'variable',
    name: 'Variable hours',
    description: 'Flexible shift workers',
    count: 25,
    contractedHours: 16,
    avgPickupHours: 8,
    hourlyRate: 11.50,
    qualifyingDays: 4,
    typicalShiftLength: 5,
    rotaProbability: 0.50,
  },
  {
    id: 'lowhours',
    name: 'Low hours (Students)',
    description: 'Students, parents - 2-4hr contracts',
    count: 15,
    contractedHours: 4,
    avgPickupHours: 6,
    hourlyRate: 11.44,
    qualifyingDays: 2,
    typicalShiftLength: 4,
    rotaProbability: 0.29, // 2/7 days
  },
];

// Backfill cost defaults
export const BACKFILL_DEFAULTS = {
  useAgency: false,
  agencyMarkup: 1.3, // 30% markup for agency staff
  overtimePremium: 1.25, // 25% overtime premium for existing staff
  defaultMethod: 'same-rate', // 'same-rate' | 'blended' | 'agency'
};

// 8-week AWE calculation helpers
export const WEEKS_FOR_AWE = 8;

// Scenario templates
export const SCENARIO_TEMPLATES = {
  hospitality: {
    name: 'Hospitality (Restaurant/Pub)',
    description: 'Typical mix for restaurant or pub operations',
    cohorts: DEFAULT_COHORTS,
    sicknessPattern: DEFAULT_SICKNESS_PATTERN,
  },
  retail: {
    name: 'Retail',
    description: 'Retail store workforce mix',
    cohorts: [
      { ...DEFAULT_COHORTS[0], count: 10 },
      { ...DEFAULT_COHORTS[1], count: 30 },
      { ...DEFAULT_COHORTS[2], count: 20 },
      { ...DEFAULT_COHORTS[3], count: 25 },
    ],
    sicknessPattern: {
      ...DEFAULT_SICKNESS_PATTERN,
      avgEpisodesPerEmployee: 2.2,
    },
  },
  healthcare: {
    name: 'Healthcare/Care Home',
    description: 'Care sector workforce',
    cohorts: [
      { ...DEFAULT_COHORTS[0], count: 40, hourlyRate: 12.00 },
      { ...DEFAULT_COHORTS[1], count: 50, hourlyRate: 11.50 },
      { ...DEFAULT_COHORTS[2], count: 20, hourlyRate: 11.44 },
    ],
    sicknessPattern: {
      ...DEFAULT_SICKNESS_PATTERN,
      avgEpisodesPerEmployee: 3.0, // Higher in care sector
    },
  },
};
